import { useGlobal } from "reactn";
import { Link } from "react-router-dom";
import "moment-duration-format";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { getProfileOverviewUrl } from "@ugg/shared/routes/app-routes";
import { normalizePatch } from "@ugg/shared/utils/patch-helpers";
import { calculateKDA, getKdaColor } from "@ugg/shared/utils/kda";
import { thousandCondense } from "@ugg/shared/utils/math";
import ItemsList from "@ugg/shared/components/common/ItemsList";
import { useSummonerMatch, SummonerMatch } from "@ugg/shared/api/requests/summoner-profiles/single-match";

import { MatchSummary, TeamData } from "@ugg/shared/api/requests/summoner-profiles/match-summaries";
import { ArenaAugment } from "@outplayed/tooltips";

import { ReactComponent as MinionsLogo } from "@ugg/shared/assets/svg/minions-logo.svg";
import { ReactComponent as PorosLogo } from "@ugg/shared/assets/svg/poros-logo.svg";
import { ReactComponent as KrugsLogo } from "@ugg/shared/assets/svg/krugs-logo.svg";
import { ReactComponent as ScuttlesLogo } from "@ugg/shared/assets/svg/scuttles-logo.svg";

type ArenaTeam = [string, number];

const PlayerRow = (props: {
  data: SummonerMatch["historicalData"]["postGameData"];
  summoner: string;
  index: number;
  mainSummoner: string;
  normalizedPatch: string;
  regionId: string;
}) => {
  const { data, summoner, index, mainSummoner, normalizedPatch, regionId } = props;
  const { getWorkingPatch, getChampionImg, getChampionName } = getRiotAssetsContext();
  const patch = getWorkingPatch();
  const color = index > 3 ? "red" : "blue";
  const matchResult = index > 3 ? "match_lose" : "match_win";
  const findPlayerAugment = data.find((player: { summonerName: string }) => player.summonerName === summoner);
  const augments = findPlayerAugment ? findPlayerAugment.augments : [0, 0, 0, 0];
  const summonerProfile = summoner === mainSummoner ? "main" : "";
  const [windowBreakpoint] = useGlobal("responsive");
  const isMobileSmall = ["MOBILE_MEDIUM", "MOBILE_SMALL"].includes(windowBreakpoint);

  let playerData = { assists: 0, championId: 0, damage: 0, damageTaken: 0, deaths: 0, gold: 0, items: [0], kills: 0, level: 0 };
  let maxDamageGiven = 0;
  let maxDamageTaken = 0;
  for (const info in data) {
    if (data[info].damage > maxDamageGiven) {
      maxDamageGiven = data[info].damage;
    }
    if (data[info].damageTaken > maxDamageTaken) {
      maxDamageTaken = data[info].damageTaken;
    }
    if (data[info].summonerName === summoner) {
      playerData = data[info];
    }
  }

  function orderItems(list: number[]) {
    let tempZero: number[] = [];
    let tempNum: number[] = [];

    for (const num in list.slice(0, 6)) {
      if (list[num] === 0) {
        tempZero.push(list[num]);
      } else {
        tempNum.push(list[num]);
      }
    }
    return [...tempNum, ...tempZero, ...list.slice(6, 7)];
  }

  const itemList = orderItems(playerData.items);

  const kdaRatio = calculateKDA(playerData.kills || 0, playerData.deaths || 0, playerData.assists || 0);
  return !isMobileSmall ? (
    <div className={`player-row _${index} ${color} ${summonerProfile}`}>
      <div className="summoner">
        <div className="champion">
          <div className="champion-face">
            <img src={getChampionImg(playerData.championId)} alt={getChampionName(playerData.championId)} />
            <div className="champion-level">{playerData.level}</div>
          </div>
        </div>
        <div className="augments">
          {augments.map((augment: number) => (
            <div className={`augment-holder ${matchResult} ${summonerProfile}`}>
              <ArenaAugment className="augment-image" augmentId={augment} patch={normalizedPatch} />
            </div>
          ))}
        </div>
        <div className="summoner-info">
          <Link
            className="summoner-name"
            title={summoner}
            to={getProfileOverviewUrl(regionId, summoner)}
            onClick={(e) => e.stopPropagation()}
          >
            {summoner}
          </Link>
          {/* <div className="summoner-rank">Holder</div> */}
        </div>
      </div>
      <div className="right-half">
        <div className="kda">
          <div className="kda-totals">
            {playerData.kills}
            <span className="slash">/</span>
            {playerData.deaths}
            <span className="slash">/</span>
            {playerData.assists}
          </div>
          <div className="kda-ratio">
            <span style={{ color: getKdaColor(kdaRatio) }}>{kdaRatio}</span> <span className="faded">KDA</span>
          </div>
        </div>
        <div className="damage">
          <div className="amount">{playerData.damage.toLocaleString()}</div>
          <div className="damage-bar">
            <div className={`top-bar ${color}`} style={{ width: `${(playerData.damage / maxDamageGiven) * 100}%` }} />
            <div
              className={`grey-bar ${color} ${summonerProfile}`}
              style={{ width: `${(1 - playerData.damage / maxDamageGiven) * 100}%` }}
            />
          </div>
        </div>
        <div className="taken">
          <div className="amount">{playerData.damageTaken.toLocaleString()}</div>
          <div className="damage-bar">
            <div className={`top-bar ${color}`} style={{ width: `${(playerData.damageTaken / maxDamageTaken) * 100}%` }} />
            <div
              className={`grey-bar ${color} ${summonerProfile}`}
              style={{ width: `${(1 - playerData.damageTaken / maxDamageTaken) * 100}%` }}
            />
          </div>
        </div>
        <div className="gold">{thousandCondense(playerData.gold)}</div>
        <div className="items">
          <div className="main-items">
            <ItemsList items={itemList.slice(0, 6)} winConditionClass={matchResult} patch={patch} dim={16} />
            <ItemsList items={[itemList[itemList.length - 1]]} winConditionClass={matchResult} patch={patch} dim={16} />
          </div>
        </div>
      </div>
    </div>
  ) : (
    <div className={`player-row _${index} ${color} ${summonerProfile} mobile-med-small`}>
      <div className="left-half">
        <div className="summoner">
          <div className="champion">
            <div className="champion-face">
              <img src={getChampionImg(playerData.championId)} alt={getChampionName(playerData.championId)} />
              <div className="champion-level">{playerData.level}</div>
            </div>
          </div>
          <div className="augments">
            {augments.map((augment: number) => (
              <div className={`augment-holder ${matchResult} ${summonerProfile}`}>
                <ArenaAugment className="augment-image" augmentId={augment} patch={normalizedPatch} />
              </div>
            ))}
          </div>
          <div className="summoner-info">
            <Link
              className="summoner-name"
              title={summoner}
              to={getProfileOverviewUrl(regionId, summoner)}
              onClick={(e) => e.stopPropagation()}
            >
              {summoner}
            </Link>
            <div className="kda">
              <div className="kda-totals">
                {playerData.kills}
                <span className="slash">/</span>
                <span className="deaths">{playerData.deaths}</span>
                <span className="slash">/</span>
                {playerData.assists}
              </div>
              <div className="kda-ratio">
                <span style={{ color: getKdaColor(kdaRatio) }}>{kdaRatio}</span> <span className="faded">KDA</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="right-half">
        <div className="main-items">
          <ItemsList items={itemList} winConditionClass={matchResult} patch={patch} dim={16} />
        </div>
        <div className="other-stats">
          <div className="gold">{thousandCondense(playerData.gold)}</div>
          <div className="damage-bar">
            <div
              className={`damage-percent ${matchResult}`}
              style={{ width: `${(playerData.damage / maxDamageGiven) * 100}%` }}
            />
            <div className="amount">{thousandCondense(playerData.damage)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function ExpandedArenaMatchCard(props: { data: MatchSummary }) {
  const { data } = props;
  const normalizedPatch = normalizePatch(data.version);
  const [windowBreakpoint] = useGlobal("responsive");
  const isMobileSmall = ["MOBILE_MEDIUM", "MOBILE_SMALL"].includes(windowBreakpoint);

  const summonerMatchData = useSummonerMatch(data.regionId, data.summonerName, data.version, data.matchId.toString());
  if (!summonerMatchData.data || summonerMatchData.loading || summonerMatchData.error) {
    return (
      <div className="expanded-match-card-container">
        <div className="loader">
          <div className="spinthatshit-loader">
            <div className="spinner" />
          </div>
        </div>
      </div>
    );
  }

  //Create correct teams variable to map
  function mergeTeams(teamA: TeamData[], teamB: TeamData[]) {
    let finalTeamPlayerRows: ArenaTeam[][] = [];

    for (let i = 1; i <= 4; i++) {
      const team: ArenaTeam[] = [];
      let subTeamId: number = 0;
      for (const member of teamA) {
        if (member.placement === i) {
          subTeamId = member.playerSubteamId;
          team.push([member.summonerName, subTeamId]);
        }
      }
      for (const member of teamB) {
        if (member.placement === i) {
          subTeamId = member.playerSubteamId;
          team.push([member.summonerName, subTeamId]);
        }
      }
      finalTeamPlayerRows.push(team);
    }

    return finalTeamPlayerRows;
  }

  const finalTeamPlayerRows = mergeTeams(data.teamA, data.teamB);
  const postGameData = summonerMatchData.data.match.historicalData.postGameData;
  const placements = ["1st", "2nd", "3rd", "4th"];
  const teamNames = ["Minions", "Poros", "Krugs", "Scuttles"];
  const teamLogos = [<MinionsLogo />, <PorosLogo />, <KrugsLogo />, <ScuttlesLogo />];

  return (
    <div className="arena-dropdown-container">
      {finalTeamPlayerRows.map((team: ArenaTeam[], index1: number) => (
        <div className="team-container" key={index1}>
          <div className="stats-header">
            <div className="team-info">
              <span className={`placement _${index1}`}>{placements[index1]}</span>
              <div className="team-logo">{teamLogos[team[0][1] - 1]}</div>
              <span className="team-name">{`(Team ${teamNames[team[0][1] - 1]})`}</span>
            </div>
            {!isMobileSmall && (
              <div className="indv-stats">
                <span className="kda">KDA</span>
                <span className="damage">Damage</span>
                <span className="taken">Taken</span>
                <span className="gold">Gold</span>
                <span className="items">Items</span>
              </div>
            )}
          </div>
          {team.map((player: ArenaTeam, index2: number) => (
            <PlayerRow
              data={postGameData}
              summoner={player[0]}
              index={index1 * 2 + index2}
              mainSummoner={data.summonerName}
              normalizedPatch={normalizedPatch}
              regionId={data.regionId}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
